import React, { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation, Pagination, Autoplay, EffectFade } from 'swiper/modules'
import { handleGetRequest } from '../../utils/api-handler.util'
import { QueryKeys } from '../../utils/queryKeyGenerator'

import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import 'swiper/css/effect-fade'
import useImagePreloader from '../../hooks/useImagePreloader'

export const BannerAds = () => {
  const { data: promotionsData } = useQuery({
    queryKey: QueryKeys.promotions(),
    queryFn: () => handleGetRequest('/promotions/carousel'),
  })
  const promotions = promotionsData?.data || []
  const { imagesPreloaded } = useImagePreloader(promotions.map((p) => p.bannerImage))

  const [imageErrors, setImageErrors] = useState(new Set())

  const handleImageError = (index) => {
    setImageErrors((prev) => new Set([...prev, index]))
  }

  if (promotions.length === 0) {
    return <></>
  }

  if (!imagesPreloaded) {
    return <></>
  }

  return (
    <div className="w-full flex justify-center">
      <div className="relative w-full">
        <Swiper
          modules={[Navigation, Pagination, Autoplay, EffectFade]}
          spaceBetween={0}
          slidesPerView={1}
          navigation={false}
          pagination={
            promotions.length > 1
              ? {
                  clickable: true,
                  dynamicBullets: true,
                  dynamicMainBullets: 5,
                }
              : false
          }
          autoplay={
            promotions.length > 1
              ? {
                  delay: 4000,
                  disableOnInteraction: false,
                  pauseOnMouseEnter: true,
                }
              : false
          }
          effect="fade"
          fadeEffect={{
            crossFade: true,
          }}
          loop={promotions.length > 1}
          className="w-full h-[180px] md:h-[250px] lg:h-[40vh]  overflow-hidden banner-ads-swiper"
          style={{
            '--swiper-navigation-size': '24px',
            '--swiper-navigation-color': '#ffffff',
            '--swiper-navigation-sides-offset': '16px',
            '--swiper-pagination-color': 'rgb(228, 127, 0)', // secondary-600
            '--swiper-pagination-bullet-inactive-color': '#ffffff',
            '--swiper-pagination-bullet-inactive-opacity': '0.6',
            '--swiper-pagination-bullet-size': '10px',
            '--swiper-pagination-bullet-horizontal-gap': '6px',
            '--swiper-pagination-bottom': '16px',
          }}
        >
          {promotions.map((promotion, index) => (
            <SwiperSlide key={promotion.id || index} className="w-full h-full">
              <div className="relative w-full h-full bg-neutral-200">
                {imageErrors.has(index) ? (
                  <div className="absolute inset-0 bg-neutral-200 flex items-center justify-center">
                    <div className="flex flex-col items-center space-y-2 text-foreground-muted">
                      <svg
                        className="w-16 h-16 opacity-50"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                      <span className="text-sm">Banner unavailable</span>
                    </div>
                  </div>
                ) : (
                  <>
                    {promotion.link ? (
                      <a href={promotion.link} target="_blank" rel="noopener noreferrer">
                        <img
                          src={promotion.bannerImage}
                          alt={promotion.title || `Banner ${index + 1}`}
                          className="w-full h-[180px] md:h-[250px] lg:h-[40vh] object-cover transition-opacity duration-300 cursor-pointer"
                          onError={() => handleImageError(index)}
                          loading="lazy"
                        />
                      </a>
                    ) : (
                      <img
                        src={promotion.bannerImage}
                        alt={promotion.title || `Banner ${index + 1}`}
                        className="w-full h-[180px] md:h-[250px] lg:h-[40vh] object-cover transition-opacity duration-300"
                        onError={() => handleImageError(index)}
                        loading="lazy"
                      />
                    )}
                  </>
                )}
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </div>
  )
}
